/**
 * Authentication Context
 * Provides Azure AD authentication state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import { setStorageItem, removeStorageItem, STORAGE_KEYS } from '../utils/storage';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  roles: string[];
  hasRole: (role: string) => boolean;
  isAdmin: boolean;
  isBilling: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider component
 * Wraps the app to provide authentication context
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const msalIsAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);

  // Use accounts array as source of truth for authentication
  // This is more reliable than useIsAuthenticated() after popup login
  const isAuthenticated = accounts.length > 0;

  useEffect(() => {
    if (accounts.length > 0) {
      const account = accounts[0];
      setUser(account);

      // Store user name/email in sessionStorage for API attribution
      // Prefer name, fall back to username (email)
      const userIdentifier = account.name || account.username || 'Unknown User';
      setStorageItem(STORAGE_KEYS.USER, userIdentifier);

      // Extract roles from ID token claims
      // Azure AD stores roles in the 'roles' claim
      const userRoles = (account.idTokenClaims as any)?.roles || [];
      setRoles(userRoles);
    } else {
      setUser(null);
      setRoles([]);
      removeStorageItem(STORAGE_KEYS.USER);
    }
    setIsLoading(false);
  }, [accounts, isAuthenticated]);

  /**
   * Detect if user is on a mobile device
   * Mobile devices should use redirect instead of popup
   */
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  /**
   * Initiates the login flow using popup (desktop) or redirect (mobile)
   */
  const login = async () => {
    try {
      setIsLoading(true);

      // Use redirect on mobile devices, popup on desktop
      if (isMobileDevice()) {
        // Use redirect with explicit redirect URI for mobile Safari compatibility
        await instance.loginRedirect({
          ...loginRequest,
          redirectUri: window.location.origin,
        });
        // Note: After redirect, execution won't continue here
        // The user will be redirected back to the app and handled in the redirect callback
      } else {
        const response = await instance.loginPopup(loginRequest);
        // Ensure the account is set as active after successful login
        if (response && response.account) {
          instance.setActiveAccount(response.account);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      // Show user-friendly error if needed
      if ((error as any)?.errorCode === 'popup_window_error') {
        console.error('Popup was blocked. Please allow popups for this site.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logs out the current user
   */
  const logout = async () => {
    try {
      // Logout using popup - redirects to login page after logout
      await instance.logoutPopup({
        postLogoutRedirectUri: "/login",
        mainWindowRedirectUri: "/login"
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Reset loading state on error
      setIsLoading(false);
    }
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: string): boolean => {
    return roles.some(r => r.toLowerCase() === role.toLowerCase());
  };

  /**
   * Check if user is an admin
   */
  const isAdmin = hasRole('Admin');

  /**
   * Check if user has billing access
   */
  const isBilling = hasRole('Billing');

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    isLoading,
    roles,
    hasRole,
    isAdmin,
    isBilling,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the auth context
 * @throws Error if used outside of AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
